import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDemoDataForCollection } from '../demoData';
import { 
  Briefcase, Trash2, CheckCircle, Filter, Plus, 
  ChevronDown, ChevronUp, Users, Send, FileText, 
  Clock, AlertCircle, Building, User, Phone, Mail, 
  Globe, MapPin, DollarSign, Award, ExternalLink, 
  Check, X, Eye, ShieldCheck, Search, Pin, Handshake
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SponsorDetailDrawer = ({ sponsor, onClose, onUpdateStatus }) => {
  if (!sponsor) return null;

  const partnerName = sponsor.partnerName || sponsor.name || 'Scholarship & Funding Partner';
  const partnerType = sponsor.partnerType || sponsor.sponsorshipType || 'Bursary Board';
  const grantAmount = sponsor.annualFundingCommitment || sponsor.financials?.grantLimit || sponsor.grantLimit || 0;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[680px] lg:w-[780px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300 text-white">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 bg-zinc-900/60 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Portal 06 &bull; Philanthropic &amp; Bursary Partner
              </span>
              <span className="text-xs font-mono font-bold text-zinc-400">
                {partnerType}
              </span>
            </div>
            <h2 className="text-2xl font-black text-white">{partnerName}</h2>
            <p className="text-xs text-zinc-400 font-bold mt-1 flex items-center gap-2">
              <Building size={14} className="text-amber-400" /> {sponsor.headquartersCity || sponsor.city || 'Nairobi'}, {sponsor.county || 'Nairobi'}, {sponsor.countryOfOperation || sponsor.country || 'Kenya'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Status</span>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                {sponsor.status || 'Active'}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Grant Budget</span>
              <span className="text-xs font-bold text-amber-400 font-mono">
                KES {Number(grantAmount).toLocaleString()}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Target Quota</span>
              <span className="text-xs font-bold text-white font-mono">
                {sponsor.studentQuota || 50} Beneficiaries
              </span>
            </div>
          </div>

          {/* Section 1: Partner Identity */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-amber-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Building size={14} /> Organization Profile &amp; Location
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Organization Name:</span>
                <span className="text-zinc-200 font-bold">{partnerName}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Entity Type:</span>
                <span className="text-zinc-200">{partnerType}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Registration / Charter No:</span>
                <span className="text-zinc-200 font-mono">{sponsor.registrationNumber || sponsor.regNo || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">KRA PIN Number:</span>
                <span className="text-zinc-200 font-mono">{sponsor.kraPinNumber || sponsor.kraPin || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Physical Address:</span>
                <span className="text-zinc-200">{sponsor.headquartersAddress || sponsor.address || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">City &amp; County:</span>
                <span className="text-zinc-200">{sponsor.headquartersCity || sponsor.city || 'Nairobi'}, {sponsor.county || 'Nairobi'}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Sponsorship Mandate */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-amber-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Award size={14} /> Sponsorship Mandate &amp; Eligibility
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Funding Mechanism:</span>
                <span className="text-zinc-200 font-bold">{sponsor.fundingMechanism || 'Full Tuition Coverage (100%)'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Target Demographics:</span>
                <span className="text-zinc-200">{sponsor.targetDemographics || sponsor.criteria || 'Underprivileged Youth'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Cohort Track Focus:</span>
                <span className="text-zinc-200">{sponsor.cohortTrackFocus || 'Class of 2026 Alpha'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Disbursement Cycle:</span>
                <span className="text-zinc-200">{sponsor.disbursementCycle || 'Milestone Based'}</span>
              </div>
            </div>
          </div>

          {/* Section 3: Liaison Contacts */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-amber-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <User size={14} /> Executive Liaison Contacts
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Primary Liaison Name:</span>
                <span className="text-zinc-200 font-bold">{sponsor.contactPersonName || sponsor.contactPerson || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Job Title:</span>
                <span className="text-zinc-200">{sponsor.contactPersonTitle || 'Director of Bursary Affairs'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Official Email:</span>
                <span className="text-zinc-200 font-mono">{sponsor.contactPersonEmail || sponsor.email || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Direct Phone:</span>
                <span className="text-zinc-200 font-mono">{sponsor.contactPersonPhone || sponsor.phone || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Admin Fee M-Pesa Ref (KES 5,000):</span>
                <span className="text-amber-400 font-mono font-bold">{sponsor.mpesaTransactionCode || sponsor.paymentReference || '-'}</span>
              </div>
            </div>
          </div>

          {/* Admin Status Switcher */}
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-zinc-400">Partner Status</span>
            <div className="flex gap-2">
              <button 
                onClick={() => onUpdateStatus(sponsor.id, 'Active')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${sponsor.status === 'Active' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Active
              </button>
              <button 
                onClick={() => onUpdateStatus(sponsor.id, 'Pending Review')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${sponsor.status === 'Pending Review' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Under Review
              </button>
              <button 
                onClick={() => onUpdateStatus(sponsor.id, 'Inactive')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${sponsor.status === 'Inactive' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SponsorManager = ({ showDemo, setShowDemo }) => {
    const navigate = useNavigate();
    const [sponsors, setSponsors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedSponsor, setSelectedSponsor] = useState(null);
    const [pinnedColumn, setPinnedColumn] = useState('partnerName');

    // Comprehensive Registration Form State (Matching 100% of onboarding.html)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [partnerName, setPartnerName] = useState('');
    const [partnerType, setPartnerType] = useState('County Bursary Board');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [kraPinNumber, setKraPinNumber] = useState('');
    const [officialWebsite, setOfficialWebsite] = useState('');
    const [headquartersAddress, setHeadquartersAddress] = useState('');
    const [headquartersCity, setHeadquartersCity] = useState('');
    const [county, setCounty] = useState('Baringo');
    const [country, setCountry] = useState('Kenya');

    const [annualFundingCommitment, setAnnualFundingCommitment] = useState(5000000);
    const [targetDemographics, setTargetDemographics] = useState('Underprivileged Youth in Devolved Counties');
    const [cohortTrackFocus, setCohortTrackFocus] = useState('Class of 2026 Alpha');
    const [studentQuota, setStudentQuota] = useState(50);
    const [fundingMechanism, setFundingMechanism] = useState('Full Tuition Coverage (100%)');
    const [disbursementCycle, setDisbursementCycle] = useState('Milestone Based (3 Tranches)');

    const [contactPersonName, setContactPersonName] = useState('');
    const [contactPersonTitle, setContactPersonTitle] = useState('Director of Bursary Administration');
    const [contactPersonEmail, setContactPersonEmail] = useState('');
    const [contactPersonPhone, setContactPersonPhone] = useState('');
    const [mpesaTransactionCode, setMpesaTransactionCode] = useState('');
    const [mouDocketUrl, setMouDocketUrl] = useState('');

    const fetchSponsors = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'sponsors'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));

            if (showDemo) {
                data.unshift(
                  {
                    id: 'baringo-bursary',
                    partnerName: 'Baringo County Bursary Board',
                    name: 'Baringo County Bursary Board',
                    partnerType: 'County Bursary Board',
                    registrationNumber: 'CG-BAR-BURS-2023',
                    kraPinNumber: 'P051100223Z',
                    officialWebsite: 'https://baringo.go.ke/bursaries',
                    headquartersAddress: 'County Government Headquarters, Kabarnet',
                    headquartersCity: 'Kabarnet',
                    county: 'Baringo',
                    countryOfOperation: 'Kenya',
                    annualFundingCommitment: 8500000,
                    grantLimit: 8500000,
                    studentQuota: 120,
                    targetDemographics: 'Baringo North, Central & Tiaty Tech Scholars',
                    fundingMechanism: 'Full Tuition Coverage (100%)',
                    disbursementCycle: 'Milestone Based (3 Tranches)',
                    contactPersonName: 'Hon. CECM Education & ICT',
                    contactPersonTitle: 'County Executive Committee Member',
                    contactPersonEmail: 'education@baringo.go.ke',
                    contactPersonPhone: '+254 722 000 111',
                    mpesaTransactionCode: 'EXEMPT-DEVOLUTION',
                    status: 'Active',
                    addedAt: '2026-06-15T09:00:00Z'
                  },
                  {
                    id: 'nef-foundation',
                    partnerName: 'National Education Foundation (NEF)',
                    name: 'National Education Foundation',
                    partnerType: 'Philanthropic Foundation',
                    registrationNumber: 'NGO/REG/1994/082',
                    kraPinNumber: 'EXEMPT-INTL-GRANT',
                    officialWebsite: 'https://nef1.org',
                    headquartersAddress: '120 Wall Street, 21st Floor',
                    headquartersCity: 'New York',
                    county: 'New York',
                    countryOfOperation: 'United States',
                    annualFundingCommitment: 25000000,
                    grantLimit: 25000000,
                    studentQuota: 500,
                    targetDemographics: 'Make Africa Great (MAG) Pan-African Scholars',
                    fundingMechanism: '90% Tuition Grant Co-Funded',
                    disbursementCycle: 'Direct Institution Wire',
                    contactPersonName: 'Dr. Katherine Welch',
                    contactPersonTitle: 'Director of African STEM Grants',
                    contactPersonEmail: 'grants@nef1.org',
                    contactPersonPhone: '+1 212 555 0199',
                    mpesaTransactionCode: 'WIRE-US-NEF-2026',
                    status: 'Active',
                    addedAt: '2026-07-01T11:30:00Z'
                  }
                );
            }
            setSponsors(data);
        } catch (e) {
            console.error('Error fetching sponsors:', e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchSponsors();
    }, [showDemo]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!partnerName.trim()) return;
        setStatusMessage('saving');

        const sponsorId = partnerName.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');

        try {
            await setDoc(doc(db, 'sponsors', sponsorId), {
                partnerName: partnerName.trim(),
                name: partnerName.trim(),
                partnerType,
                sponsorshipType: partnerType,
                registrationNumber: registrationNumber.trim(),
                kraPinNumber: kraPinNumber.trim(),
                officialWebsite: officialWebsite.trim(),
                headquartersAddress: headquartersAddress.trim(),
                headquartersCity: headquartersCity.trim(),
                county: county.trim(),
                countryOfOperation: country.trim(),
                country: country.trim(),
                annualFundingCommitment: Number(annualFundingCommitment) || 0,
                grantLimit: Number(annualFundingCommitment) || 0,
                financials: {
                    grantLimit: Number(annualFundingCommitment) || 0
                },
                studentQuota: parseInt(studentQuota) || 0,
                targetDemographics: targetDemographics.trim(),
                criteria: targetDemographics.trim(),
                cohortTrackFocus,
                fundingMechanism,
                disbursementCycle,
                contactPersonName: contactPersonName.trim(),
                contactPerson: contactPersonName.trim(),
                contactPersonTitle: contactPersonTitle.trim(),
                contactPersonEmail: contactPersonEmail.trim().toLowerCase(),
                email: contactPersonEmail.trim().toLowerCase(),
                contactPersonPhone: contactPersonPhone.trim(),
                phone: contactPersonPhone.trim(),
                mpesaTransactionCode: mpesaTransactionCode.trim(),
                paymentReference: mpesaTransactionCode.trim(),
                mouDocketUrl: mouDocketUrl.trim(),
                status: 'Active',
                updatedAt: new Date().toISOString(),
                addedAt: new Date().toISOString()
            }, { merge: true });

            setPartnerName('');
            setRegistrationNumber('');
            setHeadquartersAddress('');
            setContactPersonName('');
            setContactPersonEmail('');
            setContactPersonPhone('');
            setMpesaTransactionCode('');
            setIsFormOpen(false);

            setStatusMessage('success');
            fetchSponsors();
            setTimeout(() => setStatusMessage(''), 3000);
        } catch (e) {
            console.error('Error adding sponsor:', e);
            setStatusMessage('error');
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
      try {
        await setDoc(doc(db, 'sponsors', id), {
          status: newStatus,
          statusUpdatedAt: new Date().toISOString()
        }, { merge: true });
        
        setSponsors(prev => prev.map(s => s.id === id ? { ...s, status: newStatus } : s));
        if (selectedSponsor && selectedSponsor.id === id) {
          setSelectedSponsor(prev => ({ ...prev, status: newStatus }));
        }
      } catch (err) {
        console.error("Failed to update status:", err);
      }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this funding partner?')) return;
        try {
            await deleteDoc(doc(db, 'sponsors', id));
            if (selectedSponsor?.id === id) setSelectedSponsor(null);
            fetchSponsors();
        } catch (e) { console.error(e); }
    };

    const filteredSponsors = sponsors.filter(s => {
      const matchesType = filterType === 'all' || (s.partnerType || s.sponsorshipType || '').toLowerCase() === filterType.toLowerCase();
      const query = searchTerm.toLowerCase();
      const sName = (s.partnerName || s.name || '').toLowerCase();
      const sLoc = (s.headquartersCity || s.county || '').toLowerCase();
      const sContact = (s.contactPersonName || s.email || '').toLowerCase();
      const matchesSearch = !searchTerm || sName.includes(query) || sLoc.includes(query) || sContact.includes(query);
      return matchesType && matchesSearch;
    });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedSponsors = [...filteredSponsors].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = (a[sortConfig.key] || '').toString().toLowerCase();
        let bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="p-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-xl">
                                <Briefcase size={18} />
                            </span>
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                                Portal 06 &bull; Philanthropic &amp; Bursary Funds
                            </span>
                        </div>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight">Scholarship &amp; Funding Partners</h1>
                        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-2 max-w-xl">
                            County bursary boards, philanthropic foundations, and corporate donors funding verified student cohorts under official MoUs.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsFormOpen(!isFormOpen)}
                            className="bg-amber-600 hover:bg-amber-700 text-white font-black uppercase tracking-widest text-xs px-6 py-3.5 rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
                        >
                            <Briefcase size={16} /> {isFormOpen ? 'Close Form' : 'Register Funding Partner'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Comprehensive Registration Form */}
            {isFormOpen && (
              <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-3xl shadow-2xl space-y-6">
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                      <div>
                          <h2 className="text-base font-black uppercase tracking-widest text-amber-400 flex items-center gap-2">
                              <Briefcase size={18} /> Register Funding Partner &bull; Full Grant &amp; Bursary Schema
                          </h2>
                          <p className="text-xs text-zinc-400 mt-1">
                              Record grant limits, target demographics, funding mechanisms, liaison contacts, and admin fee references.
                          </p>
                      </div>
                      <button 
                          onClick={() => setIsFormOpen(false)}
                          className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
                      >
                          <X size={18} />
                      </button>
                  </div>

                  <form onSubmit={handleAdd} className="space-y-6">
                      {/* Section 1: Entity Profile */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-amber-400 flex items-center gap-2">
                              <Building size={14} /> 1. Organization Legal Profile &amp; Location
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Partner Organization Legal Name *</label>
                                  <input 
                                      type="text" 
                                      value={partnerName} 
                                      onChange={e => setPartnerName(e.target.value)} 
                                      placeholder="e.g. Baringo County Bursary Board / NEF Foundation" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-amber-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Partner Classification</label>
                                  <select 
                                      value={partnerType} 
                                      onChange={e => setPartnerType(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-amber-500 font-bold"
                                  >
                                      <option>County Bursary Board</option>
                                      <option>Philanthropic Foundation</option>
                                      <option>Corporate CSR Fund</option>
                                      <option>International NGO</option>
                                      <option>Religious & Community Trust</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Registration / Charter No</label>
                                  <input 
                                      type="text" 
                                      value={registrationNumber} 
                                      onChange={e => setRegistrationNumber(e.target.value)} 
                                      placeholder="e.g. CG-BAR-BURS-2023" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">KRA PIN Number</label>
                                  <input 
                                      type="text" 
                                      value={kraPinNumber} 
                                      onChange={e => setKraPinNumber(e.target.value)} 
                                      placeholder="e.g. P051100223Z" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Headquarters Street Address</label>
                                  <input 
                                      type="text" 
                                      value={headquartersAddress} 
                                      onChange={e => setHeadquartersAddress(e.target.value)} 
                                      placeholder="e.g. County Hall Block A" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">City / County</label>
                                  <input 
                                      type="text" 
                                      value={county} 
                                      onChange={e => setCounty(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 2: Sponsorship Mandate */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-amber-400 flex items-center gap-2">
                              <DollarSign size={14} /> 2. Annual Grant Limit &amp; Sponsorship Criteria
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Annual Commitment (KES) *</label>
                                  <input 
                                      type="number" 
                                      value={annualFundingCommitment} 
                                      onChange={e => setAnnualFundingCommitment(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-amber-400 font-mono font-bold text-xs focus:outline-none focus:border-amber-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Sponsored Students Quota</label>
                                  <input 
                                      type="number" 
                                      value={studentQuota} 
                                      onChange={e => setStudentQuota(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white font-mono text-xs focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Funding Mechanism</label>
                                  <select 
                                      value={fundingMechanism} 
                                      onChange={e => setFundingMechanism(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-amber-500 font-bold"
                                  >
                                      <option>Full Tuition Coverage (100%)</option>
                                      <option>90% Tuition Grant Co-Funded</option>
                                      <option>50% Matching Grant</option>
                                      <option>Partial Bursary Support</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Disbursement Cycle</label>
                                  <select 
                                      value={disbursementCycle} 
                                      onChange={e => setDisbursementCycle(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-amber-500 font-bold"
                                  >
                                      <option>Milestone Based (3 Tranches)</option>
                                      <option>Upfront Full Advance</option>
                                      <option>Monthly Staggered</option>
                                      <option>Direct Institution Wire</option>
                                  </select>
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Target Demographics / Criteria</label>
                                  <input 
                                      type="text" 
                                      value={targetDemographics} 
                                      onChange={e => setTargetDemographics(e.target.value)} 
                                      placeholder="e.g. Underprivileged Youth in Devolved Counties" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Target Cohort Track Focus</label>
                                  <input 
                                      type="text" 
                                      value={cohortTrackFocus} 
                                      onChange={e => setCohortTrackFocus(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 3: Executive Contacts & Admin Fee */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-amber-400 flex items-center gap-2">
                              <User size={14} /> 3. Executive Liaisons &amp; Fee Reference
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Liaison Executive Name *</label>
                                  <input 
                                      type="text" 
                                      value={contactPersonName} 
                                      onChange={e => setContactPersonName(e.target.value)} 
                                      placeholder="e.g. Hon. CECM Education" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-amber-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Job Title</label>
                                  <input 
                                      type="text" 
                                      value={contactPersonTitle} 
                                      onChange={e => setContactPersonTitle(e.target.value)} 
                                      placeholder="e.g. Director of Grants" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Liaison Email *</label>
                                  <input 
                                      type="email" 
                                      value={contactPersonEmail} 
                                      onChange={e => setContactPersonEmail(e.target.value)} 
                                      placeholder="grants@partner.org" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Direct Phone *</label>
                                  <input 
                                      type="tel" 
                                      value={contactPersonPhone} 
                                      onChange={e => setContactPersonPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Admin Fee M-Pesa Ref (KES 5,000)</label>
                                  <input 
                                      type="text" 
                                      value={mpesaTransactionCode} 
                                      onChange={e => setMpesaTransactionCode(e.target.value)} 
                                      placeholder="e.g. QWE8910XYZ" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-amber-400 font-mono font-bold text-xs focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Executed MoU Docket URL</label>
                                  <input 
                                      type="url" 
                                      value={mouDocketUrl} 
                                      onChange={e => setMouDocketUrl(e.target.value)} 
                                      placeholder="https://..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-amber-500"
                                  />
                              </div>
                          </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                          <button 
                              type="button" 
                              onClick={() => setIsFormOpen(false)}
                              className="px-6 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white text-xs font-black uppercase tracking-widest cursor-pointer"
                          >
                              Cancel
                          </button>
                          <button 
                              type="submit" 
                              disabled={statusMessage === 'saving'}
                              className="bg-amber-600 hover:bg-amber-700 text-white font-black uppercase tracking-widest text-xs px-8 py-3 rounded-xl transition-all shadow-lg cursor-pointer"
                          >
                              {statusMessage === 'saving' ? 'Registering...' : 'Register Funding Partner'}
                          </button>
                      </div>
                  </form>
                  {statusMessage === 'success' && (
                      <p className="text-emerald-400 text-xs font-bold flex items-center gap-1.5 justify-end">
                          <CheckCircle size={14} /> Funding partner registered successfully.
                      </p>
                  )}
              </div>
            )}

            {/* Google Sheets-Grade Modular Registry Sheet with Freeze Panes */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                {/* Spreadsheet Toolbar */}
                <div className="p-4 border-b border-zinc-800 flex flex-wrap gap-4 justify-between items-center bg-zinc-900/60">
                    <div className="flex flex-1 items-center gap-3 min-w-[280px]">
                      <Search size={16} className="text-zinc-400" />
                      <input 
                        type="text"
                        placeholder="Search by partner name, county, contact person, or KRA PIN..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Filter size={14} className="text-zinc-400" />
                        <select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            className="bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-widest px-3 py-1.5 rounded-lg focus:outline-none"
                        >
                            <option value="all">All Classifications</option>
                            <option value="county bursary board">County Bursary</option>
                            <option value="philanthropic foundation">Philanthropic</option>
                            <option value="corporate csr fund">Corporate CSR</option>
                            <option value="international ngo">International NGO</option>
                        </select>
                      </div>

                      <button 
                          onClick={fetchSponsors}
                          className="text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full border border-zinc-700 text-zinc-400 bg-zinc-900 hover:text-white transition-all shadow-sm cursor-pointer"
                      >
                          Refresh
                      </button>
                    </div>
                </div>

                {/* Horizontal Scrolling High-Density Table with Freeze Panes */}
                <div className="overflow-x-auto relative">
                  <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                      <thead>
                          <tr className="bg-zinc-900/90 border-b border-zinc-800 select-none">
                              {/* Freeze Pane: Index */}
                              <th className={`w-12 p-3 text-center text-[10px] font-black uppercase tracking-wider text-zinc-500 border-r border-zinc-800 ${
                                pinnedColumn !== 'none' ? 'sticky left-0 z-30 bg-zinc-900' : ''
                              }`}>
                                #
                              </th>

                              {/* Freeze Pane: Sponsor Name */}
                              <th className={`min-w-[220px] p-0 border-r border-zinc-800 ${
                                pinnedColumn === 'partnerName' 
                                  ? 'sticky left-12 z-30 bg-zinc-900 border-r-2 border-r-amber-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                  : ''
                              }`}>
                                <div className="w-full h-full p-3 flex items-center justify-between">
                                  <button onClick={() => handleSort('partnerName')} className="flex items-center gap-1 font-black uppercase tracking-widest text-zinc-300 hover:text-white">
                                    <span>Partner Organization</span> {sortConfig.key === 'partnerName' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                                  </button>
                                  <button
                                    onClick={() => setPinnedColumn(pinnedColumn === 'partnerName' ? 'none' : 'partnerName')}
                                    className={`p-1 rounded cursor-pointer ${pinnedColumn === 'partnerName' ? 'text-amber-400' : 'text-zinc-600 hover:text-zinc-300'}`}
                                    title={pinnedColumn === 'partnerName' ? 'Unpin column' : 'Pin Partner Name'}
                                  >
                                    <Pin size={11} className={pinnedColumn === 'partnerName' ? 'fill-amber-400' : ''} />
                                  </button>
                                </div>
                              </th>

                              {/* Classification */}
                              <th className="min-w-[150px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Entity Classification
                              </th>

                              {/* Reg No */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Reg / Charter No
                              </th>

                              {/* Location */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                City &amp; County
                              </th>

                              {/* Grant Commitment */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Annual Grant Commitment
                              </th>

                              {/* Student Quota */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Sponsored Quota
                              </th>

                              {/* Funding Mechanism */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Funding Model
                              </th>

                              {/* Target Demographics */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Target Demographics
                              </th>

                              {/* Liaison Name */}
                              <th className="min-w-[170px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Liaison Official
                              </th>

                              {/* Contact */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Contact &amp; Email
                              </th>

                              {/* Payment Ref */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Admin Fee Ref
                              </th>

                              {/* Status */}
                              <th className="min-w-[110px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Status
                              </th>

                              {/* Actions */}
                              <th className="min-w-[110px] p-3 text-center font-black uppercase tracking-widest text-zinc-400">
                                Action
                              </th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/60 font-medium">
                          {loading ? (
                              <tr><td colSpan="14" className="p-8 text-center text-zinc-500 text-xs">Loading Funding Partners...</td></tr>
                          ) : sortedSponsors.length === 0 ? (
                              <tr><td colSpan="14" className="p-8 text-center text-zinc-500 text-xs">No partners found.</td></tr>
                          ) : sortedSponsors.map((sponsor, idx) => {
                              const partnerNameVal = sponsor.partnerName || sponsor.name || 'Sponsor';
                              const grantVal = sponsor.annualFundingCommitment || sponsor.financials?.grantLimit || sponsor.grantLimit || 0;

                              return (
                                <tr 
                                    key={sponsor.id} 
                                    onClick={() => setSelectedSponsor(sponsor)}
                                    className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                                >
                                    {/* Pinned: Index */}
                                    <td className={`p-3 text-center font-mono text-[11px] text-zinc-500 border-r border-zinc-800 ${
                                      pinnedColumn !== 'none' ? 'sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900' : ''
                                    }`}>
                                        {idx + 1}
                                    </td>

                                    {/* Pinned: Name */}
                                    <td className={`p-3 border-r border-zinc-800 ${
                                      pinnedColumn === 'partnerName' 
                                        ? 'sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r-2 border-r-amber-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                        : ''
                                    }`}>
                                        <p className="font-bold text-white group-hover:text-amber-400 transition-colors">{partnerNameVal}</p>
                                        <p className="text-[10px] text-zinc-500 font-mono">{sponsor.headquartersCity || sponsor.city || 'Kenya'}</p>
                                    </td>

                                    {/* Classification */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {sponsor.partnerType || sponsor.sponsorshipType || 'Bursary Board'}
                                    </td>

                                    {/* Reg No */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-zinc-400">
                                        {sponsor.registrationNumber || sponsor.regNo || '-'}
                                    </td>

                                    {/* Location */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300 font-semibold">
                                        {sponsor.county || 'Nairobi'}, {sponsor.countryOfOperation || sponsor.country || 'Kenya'}
                                    </td>

                                    {/* Grant */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-amber-400 font-bold">
                                        KES {Number(grantVal).toLocaleString()}
                                    </td>

                                    {/* Quota */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-white font-bold">
                                        {sponsor.studentQuota || 50} Students
                                    </td>

                                    {/* Mechanism */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {sponsor.fundingMechanism || '100% Full Tuition'}
                                    </td>

                                    {/* Demographics */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-400 max-w-[180px] truncate" title={sponsor.targetDemographics || sponsor.criteria}>
                                        {sponsor.targetDemographics || sponsor.criteria || '-'}
                                    </td>

                                    {/* Liaison */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <p className="text-white font-bold">{sponsor.contactPersonName || sponsor.contactPerson || '-'}</p>
                                        <p className="text-[10px] text-zinc-500">{sponsor.contactPersonTitle || 'Director'}</p>
                                    </td>

                                    {/* Contact */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px]">
                                        <p className="text-zinc-300">{sponsor.contactPersonEmail || sponsor.email || '-'}</p>
                                        <p className="text-zinc-500">{sponsor.contactPersonPhone || sponsor.phone || '-'}</p>
                                    </td>

                                    {/* Payment Ref */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-amber-400">
                                        {sponsor.mpesaTransactionCode || sponsor.paymentReference || '-'}
                                    </td>

                                    {/* Status */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border ${
                                          sponsor.status === 'Active' 
                                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        }`}>
                                          {sponsor.status || 'Active'}
                                        </span>
                                    </td>

                                    {/* Action */}
                                    <td className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                            <button 
                                                onClick={() => setSelectedSponsor(sponsor)}
                                                className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                                title="Inspect Funding Partner"
                                            >
                                                <Eye size={12} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(sponsor.id)}
                                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                                                title="Delete Partner"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                              );
                          })}
                      </tbody>
                  </table>
                </div>
            </div>

            {/* Slide-over Full Dossier Drawer */}
            <SponsorDetailDrawer 
              sponsor={selectedSponsor} 
              onClose={() => setSelectedSponsor(null)} 
              onUpdateStatus={handleUpdateStatus}
            />
        </div>
    );
};

export default SponsorManager;
